// M-FX — currency pockets and the FX seam (v0.80).
//
// The architecture (decisions-log "M-FX"): the user never touches StableFX.
// A swap is ONE Face ID against an M-operated FX buffer on Arc; M rebalances
// that buffer against StableFX off the user's path. This module is the app-side
// seam — `FxSource` — exactly like `FundingSource` (worker) and `EarnSource`
// (app): `placeholderFxSource` ships today (quotes a rate, refuses to execute);
// `MFxSource` slots in when the MFx contract is deployed, with no UI change.
//
// Business rules encoded here:
//  * The pocket is the CURRENCY, never the token. `USDC`/`EURC`/`GBPA` never
//    appear on a surface — a pocket may even be backed by different tokens over
//    time (StableFX lists two euro tokens today), and the user must not care.
//  * USD is always on and cannot be switched off.
//  * Pockets the user has enabled are shown even when empty — an empty pocket
//    is how someone discovers Swap.

/** The ERC-20 behind a pocket on Arc. `null` = no token yet (pocket is a
 *  placeholder until M lists one). v0.80.28: USDC and EURC are both native to
 *  Arc, so USD and EUR are real; everything else waits. */
export interface CurrencyToken {
  address: `0x${string}`;
  decimals: number;
}

export interface Currency {
  /** ISO 4217 — our identity for a pocket. Never a token symbol. */
  code: string;
  /** What we print before the amount. */
  symbol: string;
  /** Plain-language name for the currency list. */
  name: string;
  /** Flag emoji (see branding: emoji now, SVG later if ever needed). */
  flag: string;
  /** Decimal places for display. */
  dp: number;
  /** The ERC-20 this pocket holds, when M has one on Arc. */
  token?: CurrencyToken;
}

// 1.4.29 (16 Oct) — every currency has its Arc testnet token (the StableFX
// survey: each held by StableFX's testnet FxEscrow). Holding and sending work
// for all of them; Swap opens per currency when M's pool for it has money
// (/fx/config → MFxSource.poolOf), so pools are stocked without an app release.
// Order here is the carousel order. USD first, then the two we lead with,
// then the rest of what StableFX quotes today (Sept 2026 list).
export const CURRENCIES: Currency[] = [
  {
    code: "USD", symbol: "$", name: "US dollar", flag: "🇺🇸", dp: 2,
    token: { address: "0x3600000000000000000000000000000000000000", decimals: 6 },
  },
  {
    code: "EUR", symbol: "€", name: "Euro", flag: "🇪🇺", dp: 2,
    token: { address: "0x89B50855Aa3bE2F677cD6303Cec089B5F319D72a", decimals: 6 },
  },
  {
    // 1.4.26 (16 Oct) — the pound is real: Agant's GBPA on Arc testnet (StableFX
    // lists and trades it; M's swap pool stocked £39.44 via /fx/ops the same day).
    code: "GBP", symbol: "£", name: "British pound", flag: "🇬🇧", dp: 2,
    token: { address: "0xa42e82b5D25E84d107Cd8549CA432ef489CbaD32", decimals: 6 },
  },
  { code: "CHF", symbol: "CHF ", name: "Swiss franc", flag: "🇨🇭", dp: 2, token: { address: "0x74ef206336F87843485E5f3fdaEA13ba4ec309E7", decimals: 6 } },
  { code: "CAD", symbol: "C$", name: "Canadian dollar", flag: "🇨🇦", dp: 2, token: { address: "0x23d7CFFd0876f3ABb6B074287ba2aeefBc83825d", decimals: 6 } },
  { code: "AUD", symbol: "A$", name: "Australian dollar", flag: "🇦🇺", dp: 2, token: { address: "0xd2a530170D71a9Cfe1651Fb468E2B98F7Ed7456b", decimals: 6 } },
  { code: "MXN", symbol: "MX$", name: "Mexican peso", flag: "🇲🇽", dp: 2, token: { address: "0x836F73Fbc370A9329Ba4957E47912DfDBA6BA461", decimals: 6 } },
  { code: "BRL", symbol: "R$", name: "Brazilian real", flag: "🇧🇷", dp: 2, token: { address: "0x8629020763F6239643a02e664a25BF4AD7787254", decimals: 18 } },
  { code: "ZAR", symbol: "R", name: "South African rand", flag: "🇿🇦", dp: 2, token: { address: "0x47b025D6002234a5038bCD94767bd82b27C2b96F", decimals: 18 } },
  { code: "SEK", symbol: "kr ", name: "Swedish krona", flag: "🇸🇪", dp: 2, token: { address: "0xD35141583f374d401fee1BFdDc35910E1D33a588", decimals: 6 } },
  { code: "KRW", symbol: "₩", name: "South Korean won", flag: "🇰🇷", dp: 0, token: { address: "0xC5bD9EBB09446F5F94E3b3D899072fC2eC5d3a1a", decimals: 18 } },
];

export const BASE_CURRENCY = "USD";

// 1.4.51 (Alexander, 20 Oct) — the stablecoin an M® Note's face names: "Backed 1:1 by USDC on Arc", EURC for
// a euro note. The coins with a name of their own; any other currency's note would say its code.
const COINS: Record<string, string> = { USD: "USDC", EUR: "EURC", GBP: "GBPA" };
export function coinName(code: string): string {
  return COINS[code] ?? `${code} stablecoin`;
}
/** The face's second line. */
export function backingLine(code: string): string {
  return `Backed 1:1 by ${coinName(code)} on Arc`;
}

/** The currencies M can actually hold and move today. */
export function liveCurrencies(): string[] {
  return CURRENCIES.filter((c) => c.token).map((c) => c.code);
}

export function currency(code: string): Currency {
  return CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0];
}

/** 1.4.30 (Alexander, 16 Oct: "here's what the ECB uses") — a rate to about
 *  five significant figures, as the ECB quotes them: ₩1,428.57, R17.668,
 *  R$5.0674, €0.8866 (four decimals below 1, never fewer than two). */
export function rateDecimals(rate: number): number {
  const a = Math.abs(rate);
  if (!(a >= 1)) return 4;
  const intDigits = Math.floor(Math.log10(a)) + 1;
  return Math.max(2, 5 - intDigits);
}
export function formatRateFx(code: string, rate: number): string {
  const dp = rateDecimals(rate);
  return `${currency(code).symbol}${rate.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}

/** `$61.20`, `€0.00`, `₩12,000`. The currency's own symbol, never a token. */
export function formatFx(code: string, amount: number): string {
  const c = currency(code);
  const n = amount.toLocaleString("en-US", {
    minimumFractionDigits: c.dp,
    maximumFractionDigits: c.dp,
  });
  return `${c.symbol}${n}`;
}

// --- which pockets this device shows -----------------------------------------
// Device-local like `m.nick` / `m.emoji`; joins the E2E sync store when that
// lands (v0.80 item 1).
const KEY = "m.currencies";
const DEFAULT_ON = ["USD", "EUR", "GBP"];

export function enabledCurrencies(): string[] {
  let list: string[] = DEFAULT_ON;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        list = parsed.filter((c): c is string => typeof c === "string");
      }
    }
  } catch {
    /* default */
  }
  // USD is always on, always first; drop anything we no longer support.
  const known = list.filter(
    (c) => c !== BASE_CURRENCY && CURRENCIES.some((x) => x.code === c),
  );
  return [BASE_CURRENCY, ...known];
}

// --- which pocket the Balance panel opens on ----------------------------------
// v0.80.13 — set by press-and-hold in the currency list. Device-local, like
// `m.currencies`. Falls back to USD whenever the stored pocket isn't enabled.
const DEFAULT_KEY = "m.currency";

export function defaultCurrency(): string {
  try {
    const v = localStorage.getItem(DEFAULT_KEY);
    if (v && enabledCurrencies().includes(v)) return v;
  } catch {
    /* fall through */
  }
  return BASE_CURRENCY;
}

// 1.5.41 (Alexander, 29 Oct: "if the wallet is accessed through the M® Note taking link … show balance of that
// currency pocket. In other cases, at open, we show the default currency balance.") — the home opens on the default
// pocket (press-and-hold in the currency list; dollars when none), except the one home a note take opens: that home
// opens on the note's pocket, once, as a pocket pick would (for that home only). Supersedes 1.4.63, which opened every
// home in the currency of the last transaction this device knew (`m.lastCcy`, written by every send and payment).
const OPEN_KEY = "m.openCcy";
const OLD_LAST_KEY = "m.lastCcy"; // 1.4.63's key — cleared at the next home
const OPEN_KEEP_MS = 24 * 3_600_000;

/** A note take is handing over to the home: open it on the note's pocket (opened here if it was closed). */
export function openHomeOn(address: string, code: string | null | undefined): void {
  if (!code || !CURRENCIES.some((c) => c.code === code)) return;
  setCurrencyEnabled(code, true); // a franc note opens the franc pocket, so its money and its line make sense
  try { localStorage.setItem(OPEN_KEY, JSON.stringify({ a: address.toLowerCase(), at: Date.now(), ccy: code })); } catch { /* the default pocket */ }
}

/** The pocket a take left for this wallet's next home, read once (and gone); else null — the default pocket. */
export function takeOpenPocket(address: string): string | null {
  try {
    localStorage.removeItem(OLD_LAST_KEY);
    const raw = localStorage.getItem(OPEN_KEY);
    if (!raw) return null;
    localStorage.removeItem(OPEN_KEY);
    const v = JSON.parse(raw) as { a?: string; at?: number; ccy?: string } | null;
    if (!v || (v.a ?? "") !== address.toLowerCase() || typeof v.at !== "number" || Date.now() - v.at > OPEN_KEEP_MS) return null;
    return v.ccy && enabledCurrencies().includes(v.ccy) ? v.ccy : null;
  } catch {
    return null;
  }
}

// v0.80.29 — the pocket you last opened in the Balance panel. It steers the
// home keypad and Send for this session only; it is never persisted, so the
// next launch is back on the default pocket.
let sessionCurrency: string | null = null;

/** The currency the home screen is working in right now. */
export function activeCurrency(): string {
  if (sessionCurrency && enabledCurrencies().includes(sessionCurrency)) return sessionCurrency;
  return defaultCurrency();
}

/** Called when a pocket is viewed; pass null to fall back to the default. */
export function setSessionCurrency(code: string | null): void {
  sessionCurrency = code;
}

export function setDefaultCurrency(code: string): void {
  if (!CURRENCIES.some((c) => c.code === code)) return;
  sessionCurrency = null; // choosing a default is a stronger statement than "last viewed"
  try {
    localStorage.setItem(DEFAULT_KEY, code);
    void import("./settings").then((m) => m.touch("currency")); // v0.80.40
  } catch {
    /* non-fatal */
  }
}

export function setCurrencyEnabled(code: string, on: boolean): void {
  if (code === BASE_CURRENCY) return; // never switchable
  const set = new Set(enabledCurrencies());
  if (on) set.add(code);
  else set.delete(code);
  // Persist in CURRENCIES order so the carousel is stable.
  const ordered = CURRENCIES.filter((c) => set.has(c.code)).map((c) => c.code);
  try {
    localStorage.setItem(KEY, JSON.stringify(ordered));
    void import("./settings").then((m) => m.touch("currencies")); // v0.80.40
  } catch {
    /* non-fatal */
  }
  // A pocket that's been switched off can't stay the default.
  if (!on && defaultCurrency() === code) setDefaultCurrency(BASE_CURRENCY);
  // v1.1.30 — when each pocket was added (the pockets row's second pill).
  const added = readMap(ADDED_KEY);
  if (on) added[code] = Date.now();
  else delete added[code];
  writeMap(ADDED_KEY, added);
}

// --- v1.1.30 — the pockets row (Alexander, 5 Oct 18:55) ------------------------
// Pill 1 = the default pocket. Pill 2 = the pocket added most recently. Pill 3 =
// the one used most, apart from the default; USD when neither of the first two
// is USD. A pick from pill 2's or pill 3's list sets that pill until another
// currency is picked there. Device-local, like `m.currencies`.
const ADDED_KEY = "m.pkAdded"; // { code: ms } — the three majors have none (added at the start)
const USE_KEY = "m.pkUse"; // { code: count } — sends and swaps made in that pocket
const SLOT_KEY = "m.pkSlots"; // { "2": code, "3": code } — the pills' own picks

function readMap(key: string): Record<string, number | string> {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? "{}") as unknown;
    return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, number | string>) : {};
  } catch {
    return {};
  }
}
function writeMap(key: string, v: Record<string, number | string>): void {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* non-fatal */ }
}

/** A payment or a swap was made in this pocket. */
export function notePocketUse(code: string): void {
  const m = readMap(USE_KEY);
  m[code] = (Number(m[code]) || 0) + 1;
  writeMap(USE_KEY, m);
}

/** Pill 2 or 3 was set from its list: it shows that pocket until another is picked there. */
export function setPocketSlot(slot: 2 | 3, code: string): void {
  const row = pocketRow();
  const s = readMap(SLOT_KEY);
  const other = slot === 2 ? 3 : 2;
  // The same pocket can't sit in both pills: the other pill takes this one's old pocket.
  if (row[other - 1] === code && row[slot - 1]) s[String(other)] = row[slot - 1];
  s[String(slot)] = code;
  writeMap(SLOT_KEY, s);
}

/** The pure rule, for the tests. */
export function pocketRowOf(
  enabled: string[],
  def: string,
  added: Record<string, number>,
  uses: Record<string, number>,
  slots: { 2?: string; 3?: string },
): string[] {
  const order = (c: string) => enabled.indexOf(c);
  const rest = enabled.filter((c) => c !== def);
  const row: string[] = [def];
  const ok = (c: string | undefined): c is string => !!c && rest.includes(c) && !row.includes(c);
  // Pill 2 — its own pick, else the most recently added (ties: the list's order).
  const second = ok(slots[2])
    ? slots[2]
    : [...rest].sort((a, b) => (added[b] ?? 0) - (added[a] ?? 0) || order(a) - order(b))[0];
  if (second) row.push(second);
  // Pill 3 — its own pick, else USD when neither pill is USD, else the most used.
  const free = rest.filter((c) => !row.includes(c));
  let third: string | undefined;
  if (ok(slots[3])) third = slots[3];
  else if (!row.includes(BASE_CURRENCY) && free.includes(BASE_CURRENCY)) third = BASE_CURRENCY;
  else third = [...free].sort((a, b) => (uses[b] ?? 0) - (uses[a] ?? 0) || order(a) - order(b))[0];
  if (third) row.push(third);
  return row;
}

/** Up to three pills: the default pocket, the newest, the most used (see above). */
export function pocketRow(): string[] {
  const num = (m: Record<string, number | string>) =>
    Object.fromEntries(Object.entries(m).map(([k, v]) => [k, Number(v) || 0]));
  const s = readMap(SLOT_KEY);
  return pocketRowOf(
    enabledCurrencies(),
    defaultCurrency(),
    num(readMap(ADDED_KEY)),
    num(readMap(USE_KEY)),
    { 2: typeof s["2"] === "string" ? s["2"] : undefined, 3: typeof s["3"] === "string" ? s["3"] : undefined },
  );
}

// --- the seam ----------------------------------------------------------------

export interface FxQuote {
  from: string;
  to: string;
  /** Units of `to` per one unit of `from`, inclusive of M's spread. */
  rate: number;
  /** What leaves the From pocket. */
  amountIn: number;
  /** What the user receives, after fees. */
  amountOut: number;
  /** Fee expressed in the FROM currency; null when we only show "incl. fees". */
  fee: number | null;
  /** When this quote stops being offerable (ms epoch). */
  expiresAt: number;
}

/** v0.90.19 — what rung 3 of the ladder could cover right now, for one pair. */
export interface FxCapacity {
  /** Units of `to` per one unit of `from`, inclusive of M's spread. */
  rate: number;
  /** Per-swap cap in `from` units; null = no cap. */
  maxIn: number | null;
  /** Swaps this wallet has left; null = unlimited. 0 = the rung is absent. */
  remaining: number | null;
  /** The buffer's balance in `to`; null = unknown (then the voucher decides). */
  bufferOut: number | null;
}

/** v0.90.19 — a signed top-up: exactly `amountOut` of `to`, priced by the
 *  Worker, as calls the payment folds into its own userOp. */
export interface FxTopUp {
  from: string;
  to: string;
  amountIn: number;
  amountOut: number;
  nonce: string;
  calls: { to: string; data: string }[];
}

/** 1.4.25 — a swap made ready before the slide: the signed voucher and the
 *  prepared userOp. `quote.amountOut` is the voucher's figure. */
export interface FxPrepared {
  quote: FxQuote;
  amountOut: number;
  nonce: string;
  calls: { to: string; data: string }[];
  op: import("./adapters/WalletAdapter").PreparedOp | null;
  /** ms epoch: after this, prepare again rather than slide on it. */
  usableUntil: number;
}

export interface FxSource {
  /** Balances for every enabled pocket, keyed by currency code. */
  getPockets(address: string): Promise<Record<string, number>>;
  /** v0.90.19 — the ladder of sources. Absent on sources that can't convert. */
  capacity?(from: string, to: string): Promise<FxCapacity>;
  topUp?(from: string, to: string, amountOut: number): Promise<FxTopUp>;
  reportTopUp?(t: FxTopUp, txHash: string, ok: boolean): void;
  /** An indicative, user-facing rate — already carries M's spread. */
  quote(from: string, to: string, amount: number): Promise<FxQuote>;
  /** Execute the swap (one Face ID). Throws FxUnavailableError until MFx is live.
   *  v0.90 — `amountOut` is what the signed voucher actually paid, which is
   *  what the success toast should report: the card's figure was a quote, this
   *  is the settlement. Omitted by sources that can't know it. */
  /** 1.4.29 — M's pool in a currency; null = unknown. */
  poolOf?(code: string): Promise<number | null>;
  /** 1.4.25 — prepare before the slide (absent on sources that can't). */
  canPrepare?(): Promise<boolean>;
  prepareSwap?(q: FxQuote, opts?: { quotedOut?: number; noOp?: boolean }): Promise<FxPrepared>;
  sendSwap?(p: FxPrepared, onStage?: (stage: "confirming") => void): Promise<{ txHash: string; amountOut?: number }>;
  /** v2.0 — the finger on the thumb: ask for the sponsor's signature of the prepared userOp (never throws). */
  touch?(p: FxPrepared): void;
  /** 1.4.23 — `onStage("confirming")` once Face ID is given and the operation is on its way. */
  swap(quote: FxQuote, onStage?: (stage: "confirming") => void): Promise<{ txHash: string; amountOut?: number }>;
  /** Whether swaps can actually be executed right now. */
  readonly live: boolean;
}

export class FxUnavailableError extends Error {
  constructor(msg = "fx_not_enabled") {
    super(msg);
  }
}

/**
 * Ships in v0.80.1. Reads the USD pocket from the caller (the wallet already
 * knows its USDC balance) and reports every other pocket as empty, because
 * without MFx there is nowhere for those balances to live yet. Quotes come
 * from a static indicative table so the sheet is fully designed and testable;
 * `swap()` refuses. Replaced wholesale by `MFxSource`.
 */
export function makePlaceholderFxSource(usdBalance: () => number): FxSource {
  // Indicative only — real rates come from the Worker (/fx/rate), which reads
  // StableFX. Deliberately NOT presented as tradable anywhere.
  const PER_USD: Record<string, number> = {
    USD: 1,
    EUR: 0.893,
    GBP: 0.771,
    CHF: 0.812,
    CAD: 1.404,
    AUD: 1.512,
    MXN: 17.85,
    BRL: 5.42,
    ZAR: 18.1,
    SEK: 9.63,
    KRW: 1338,
  };
  return {
    live: false,
    async getPockets() {
      const out: Record<string, number> = {};
      for (const code of enabledCurrencies()) {
        out[code] = code === BASE_CURRENCY ? usdBalance() : 0;
      }
      return out;
    },
    async quote(from, to, amount) {
      const rate = (PER_USD[to] ?? 1) / (PER_USD[from] ?? 1);
      return {
        from,
        to,
        rate,
        amountIn: amount,
        amountOut: amount * rate,
        fee: null, // "incl. fees" until the Worker returns a real figure
        expiresAt: Date.now() + 60_000,
      };
    },
    async swap() {
      throw new FxUnavailableError();
    },
  };
}
